import { useState } from "react";
import { Button } from "@shoptet/ui";
import { Trans, useTranslation } from "react-i18next";
import { useCreditsQuery } from "../../graphql/generated/graphql";
import { formatCurrencyUnit, formatMoney } from "../../i18n/formatNumbers";
import { PlusIcon } from "../Icons";
import { SmallLoader } from "../SmallLoader";
import { getRemainingCreditDays } from "./getRemainingCreditDays";
import { CreditModal } from "./Modal";

type CreditPropsT = {
  fullWidth?: boolean;
  showInfo?: boolean;
  useRecommendedMonthlyBudget?: boolean;
};
export const Credits = ({ fullWidth, showInfo, useRecommendedMonthlyBudget }: CreditPropsT) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.Credits" });
  const [modalOpened, setModalOpened] = useState(false);
  const [creditModalCounter, setCreditModalCounter] = useState(0);

  const { data, loading } = useCreditsQuery();
  const remainingCreditDays = getRemainingCreditDays(data?.organization);
  if (loading || !data?.organization) {
    return <SmallLoader />;
  }

  const hasBoughtAnyCredits = (data.organization.boughtCredits.amount || 0) > 0;
  if (loading || !data?.organization) {
    return <SmallLoader />;
  }

  return (
    <>
      {showInfo && (
        <>
          <h2>{t("Current credit balance Headline")}</h2>
        </>
      )}
      <div>
        <div style={fullWidth ? {} : { justifyContent: "center", display: "flex" }}>
          <div
            style={{
              border: "1px solid var(--app-border)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minWidth: "267px",
              position: "relative",
              padding: "10px",
              margin: "20px 0",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "-10px",
                left: "50%",
                transform: "translateX(-50%)",
                color: "var(--app-text)",
                background: "var(--app-surface)",
                padding: "0px 10px",
              }}
            >
              {t("BoxLabel")}
            </div>
            <div
              data-test-id="current-credit-balance"
              style={{ textAlign: "center", display: "flex", alignItems: "center", margin: "10px 0" }}
            >
              <span style={{ color: "var(--app-primary)", fontSize: "24px", fontWeight: 700 }}>
                {formatMoney({ amount: data.organization.creditBalance.amount || 0 })}
              </span>
              <span style={{ color: "var(--app-text-subtle)", paddingLeft: "5px" }}>
                {data.organization.creditBalance.currency
                  ? formatCurrencyUnit(data.organization.creditBalance.currency)
                  : ""}
              </span>
            </div>
            {showInfo && remainingCreditDays > 7 && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <div style={{ color: "var(--app-brand-green)" }}>
                  <Trans
                    components={{ strong: <strong /> }}
                    i18nKey="Credit will run out in"
                    t={t}
                    values={{ remainingCreditDays }}
                  />
                </div>
              </div>
            )}
            {showInfo && hasBoughtAnyCredits && remainingCreditDays <= 7 && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <div style={{ color: "var(--app-critical)" }}>
                  <Trans
                    components={{ strong: <strong /> }}
                    i18nKey="Credit will suffice for"
                    t={t}
                    values={{ remainingCreditDays }}
                  />
                </div>
              </div>
            )}
            <div style={{ margin: "10px 0" }}>
              <Button
                data-test-id="credit-onetime-increase-button"
                variant={"action"}
                onClick={() => setModalOpened(true)}
              >
                <span style={{ display: "flex", alignItems: "center" }}>
                  <PlusIcon /> &nbsp; {t("Increase credits button")}
                </span>
              </Button>
            </div>
          </div>
        </div>
        <CreditModal
          key={`credit-modal-${creditModalCounter}`}
          isOpen={modalOpened}
          useRecommendedMonthlyBudget={useRecommendedMonthlyBudget}
          onClose={() => {
            setModalOpened(false);
            setCreditModalCounter((counter) => counter + 1);
          }}
        />
      </div>
    </>
  );
};
